// useGalleryCardLoadGate.js

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const GALLERY_CARD_LOAD_ROOT_MARGIN = '200px';

/**
 * useGalleryCardLoadGate
 *
 * Defers gallery card media work until the card enters the viewport.
 *
 * @param {string} resetKey - Changes to this value reset the load gate.
 *
 * @returns {{ containerRef: import('react').RefObject<HTMLElement|null>, shouldLoad: boolean }}
 */
const useGalleryCardLoadGate = (resetKey = '') => {
    const containerRef = useRef(null);
    const [shouldLoad, setShouldLoad] = useState(false);

    useLayoutEffect(() => {
        setShouldLoad(false);
    }, [resetKey]);

    useEffect(() => {
        const nodeEl = containerRef.current;
        if (!nodeEl) {
            return undefined;
        }

        if (typeof IntersectionObserver === 'undefined') {
            setShouldLoad(true);
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.target !== nodeEl || !entry.isIntersecting) return;
                    setShouldLoad(true);
                });
            },
            {
                root: null,
                rootMargin: GALLERY_CARD_LOAD_ROOT_MARGIN,
                threshold: 0,
            },
        );

        observer.observe(nodeEl);

        return () => observer.disconnect();
    }, [resetKey]);

    return {
        containerRef,
        shouldLoad,
    };
};

export default useGalleryCardLoadGate;
